import {
  Component,
  Input,
  Output,
  OnInit,
  OnDestroy,
  EventEmitter,
  ViewContainerRef,
  ViewChild,
  ContentChild,
  ElementRef,
  AfterContentInit,
} from "@angular/core";
import { BaseChartSettings } from "app/shared/model/charts/user-analytic-configurations.model";
import {
  ChartDefinition,
  DisplayChartType,
} from "../../chartDefinitions.config";
import { ColorPickerService } from "ngx-color-picker";
import { ModalDirective } from "ngx-bootstrap/modal";
import { ColorSchemes } from "app/analytics/plugins/colorschemes/colorschemes";
import { ToastrService } from "app/shared";
import { TooltipCoordinatorService } from "app/analytics/services/tooltip-coordinator.service";

@Component({
  selector: "base-details",
  templateUrl: "./base-details.component.html",
  styleUrl: "./base-details.component.css",
  standalone: false,
})
export class BaseDetailsComponent
  implements OnInit, OnDestroy, AfterContentInit
{
  @ContentChild("filterContent", { static: false, read: ElementRef })
  filterContent: ElementRef;
  hasFilterContent = false;

  ngAfterContentInit(): void {
    // Check if filter content was projected
    this.hasFilterContent = !!this.filterContent;
  }
  @ViewChild("colorSchemeModal") verifySurveyModal: ModalDirective;
  @Input() chartDetails: ChartDefinition;

  private _generalSettings: BaseChartSettings;
  @Input() set generalSettings(value: BaseChartSettings) {
    // When labels from existing config are removed, changees aren't reflected
    // in legend labels -> remove here to handle

    if (value && value.legendLabels) {
      const labelOptionsSet = new Set(this.chartDetails.labelOptions);
      value.legendLabels = value.legendLabels.filter((label) =>
        labelOptionsSet.has(label)
      );
    }

    this._generalSettings = value;
    if (value && Array.isArray(value.legendLabels)) {
      if (value.legendLabels.length === 0) {
        this.selectedLabelSetOptions = [...this.chartDetails.defaultLabels];
        this._generalSettings.legendLabels = [
          ...this.chartDetails.defaultLabels,
        ];
      } else {
        this.selectedLabelSetOptions = [...value.legendLabels];
      }
    }
    if (value && Array.isArray(value.displayLabels)) {
      this.selectedLabelSetOptions2 = [...value.displayLabels];
    }
    if (value.colorScheme) {
      this.selectedColorScheme =
        this.colorSchemeObject[this.colorScheme.split(".")[0]][
          this.colorScheme.split(".")[1]
        ];
    }
  }
  get generalSettings(): BaseChartSettings {
    return this._generalSettings;
  }
  @Input() showSharedSettings: boolean = true;

  @Output() generalSettingsChange: EventEmitter<BaseChartSettings> =
    new EventEmitter<BaseChartSettings>();

  @Output() chartTypeChange: EventEmitter<string> = new EventEmitter<string>();

  labelSetOptions: string[] = [];
  selectedLabelSetOptions: string[] = [];
  selectedLabelSetOptions2: string[] = [];
  DisplayChartTypeEnum = DisplayChartType;
  chartTypeOptions: DisplayChartType[] = [];
  legendPositionOptions: string[] = ["top", "left", "bottom", "right"];
  objectKeys = Object.keys;
  colorSchemeObject = ColorSchemes;
  minColors: number = 0;
  hasChanges: boolean = false;

  private _toastr: ToastrService;
  constructor(
    public vcRef: ViewContainerRef,
    private cpService: ColorPickerService,
    private toastr: ToastrService,
    private tooltipCoordinator: TooltipCoordinatorService
  ) {
    this._toastr = toastr;
  }

  //example of toastr
  //this._toastr.displayToastrMessage('error', 'Message', 'Title');

  emitSettingsChange(): void {
    this.generalSettingsChange.emit(this.generalSettings);
  }

  //Note: These settings should be maintained in the same order they are maintained user-analytic-configurations.model.ts
  get selectedChartType(): string | DisplayChartType {
    return (
      this.generalSettings?.chartType ||
      this.chartDetails?.chartTypeOptions[0] ||
      DisplayChartType.Bar
    );
  }

  set selectedChartType(value: string | DisplayChartType) {
    this.generalSettings.chartType = value;
    this.emitSettingsChange();
    this.chartTypeChange.emit(value);
  }

  get responsive(): boolean {
    return (
      this.generalSettings?.responsive || BaseChartSettings.defaults.responsive
    );
  }

  set responsive(value: boolean) {
    this.generalSettings.responsive = value;
    this.emitSettingsChange();
  }

  private _selectedColorScheme: string[];
  get selectedColorScheme(): string[] {
    return this._selectedColorScheme || this.colorSchemeObject.other.default;
  }

  set selectedColorScheme(value: string[]) {
    this._selectedColorScheme = value;
  }

  get colorScheme(): string {
    return (
      this.generalSettings?.colorScheme ||
      BaseChartSettings.defaults.colorScheme
    );
  }

  set colorScheme(value: string) {
    this.generalSettings.colorScheme = value;
    this.selectedColorScheme =
      this.colorSchemeObject[this.colorScheme.split(".")[0]][
        this.colorScheme.split(".")[1]
      ];
    this.cancelColorSchemePickerModal();
    this.emitSettingsChange();
  }

  selectColorScheme(group: string, name: string, colorScheme: string[]): void {
    this.selectedColorScheme = colorScheme;
    this.colorScheme = `${group}.${name}`;
    this.cancelColorSchemePickerModal();
  }

  // get extendedColorScheme(): string {
  //   return (
  //     this.generalSettings?.extendedColorScheme ||
  //     BaseChartSettings.defaults.extendedColorScheme
  //   );
  // }

  // set extendedColorScheme(value: "data" | "dataset" | "label") {
  //   this.generalSettings.extendedColorScheme = value;
  //   this.emitSettingsChange();
  // }

  get showLegend(): boolean {
    return (
      this.generalSettings?.showLegend || BaseChartSettings.defaults.showLegend
    );
  }

  set showLegend(value: boolean) {
    this.generalSettings.showLegend = value;
    this.emitSettingsChange();
  }

  get legendPosition(): string {
    return (
      this.generalSettings?.legendPosition ||
      BaseChartSettings.defaults.legendPosition
    );
  }

  set legendPosition(value: "left" | "right" | "bottom" | "top") {
    this.generalSettings.legendPosition = value;
    this.emitSettingsChange();
  }

  //note: If performance issues are observed, consider different options. These seems to run getBackgroundColor repeatedly
  get backgroundColor(): string {
    return (
      this.generalSettings?.backgroundColor ||
      BaseChartSettings.defaults.backgroundCanvasColor
    );
  }

  set backgroundColor(value: string) {
    this.generalSettings.backgroundColor = value;
    this.emitSettingsChange();
  }

  setDefaultBgColor(): void {
    this.backgroundColor = BaseChartSettings.defaults.backgroundCanvasColor;
  }

  get showBorder(): boolean {
    if (
      this.generalSettings?.chartType === DisplayChartType.Pie ||
      this.generalSettings?.chartType === DisplayChartType.Doughnut
    ) {
      return (
        this.generalSettings?.showBorder ||
        BaseChartSettings.defaults.showBorder
      );
    }

    return false;
  }

  set showBorder(value: boolean) {
    this.generalSettings.showBorder = value;
    this.emitSettingsChange();
  }

  get borderColor(): string {
    return (
      this.generalSettings?.borderColor ||
      BaseChartSettings.defaults.borderColor
    );
  }

  set borderColor(value: string) {
    this.generalSettings.borderColor = value;
    this.emitSettingsChange();
  }

  setDefaultBorderColor(): void {
    this.borderColor = BaseChartSettings.defaults.borderColor;
  }

  get padding(): { top: number; right: number; bottom: number; left: number } {
    return this.generalSettings?.padding || BaseChartSettings.defaults.padding;
  }

  set padding(value: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  }) {
    this.generalSettings.padding = value;
    this.emitSettingsChange();
  }

  get showLabels(): boolean {
    return (
      this.generalSettings?.showLabels || BaseChartSettings.defaults.showLabels
    );
  }

  set showLabels(value: boolean) {
    this.generalSettings.showLabels = value;
    this.emitSettingsChange();
  }

  get labelFontColor(): string {
    return (
      this.generalSettings?.labelFontColor ||
      BaseChartSettings.defaults.labelFontColor
    );
  }

  setDefaultLabelFontColor(): void {
    this.labelFontColor = BaseChartSettings.defaults.labelFontColor;
  }

  set labelFontColor(value: string) {
    this.generalSettings.labelFontColor = value;
    this.emitSettingsChange();
  }

  get labelFontSize(): number {
    return (
      this.generalSettings?.labelFontSize ||
      BaseChartSettings.defaults.labelFontSize
    );
  }

  set labelFontSize(value: number) {
    this.generalSettings.labelFontSize = value;
    this.emitSettingsChange();
  }

  get labelAnchorPosition(): string {
    return (
      this.generalSettings?.labelAnchorPosition ||
      BaseChartSettings.defaults.labelAnchorPosition
    );
  }

  set labelAnchorPosition(value: "center" | "start" | "end") {
    this.generalSettings.labelAnchorPosition = value;
    this.emitSettingsChange();
  }

  get labelAlignment(): string {
    return (
      this.generalSettings?.labelAlignment ||
      BaseChartSettings.defaults.labelAlignment
    );
  }

  set labelAlignment(
    value: "center" | "start" | "end" | "right" | "bottom" | "left" | "top"
  ) {
    this.generalSettings.labelAlignment = value;
    this.emitSettingsChange();
  }

  get labelOffset(): number {
    return (
      this.generalSettings?.labelOffset ||
      BaseChartSettings.defaults.labelOffset
    );
  }

  set labelOffset(value: number) {
    this.generalSettings.labelOffset = value;
    this.emitSettingsChange();
  }

  toggleLabelOption(option: string, event: Event): void {
    const index = this.selectedLabelSetOptions.indexOf(option);
    if (index > -1) {
      // prevent unchecking if it's the last selected option
      if (this.selectedLabelSetOptions.length > 1) {
        this.selectedLabelSetOptions.splice(index, 1); // Deselect, require at least one label
        this.generalSettings.legendLabels = this.selectedLabelSetOptions;
        this.emitSettingsChange();
      } else {
        this._toastr.displayToastrMessage(
          "warning",
          "At least one label/column must be selected",
          "Unable to deselect label"
        );
        (event.target as HTMLInputElement).checked = true; // Revert the checkbox state
      }
    } else {
      this.selectedLabelSetOptions.push(option); // Select
      this.generalSettings.legendLabels = this.selectedLabelSetOptions;
      this.emitSettingsChange();
    }
    //(`******changed labels`, this.generalSettings.legendLabels)
  }

  toggleLabelOption2(option: string): void {
    const index = this.selectedLabelSetOptions2.indexOf(option);
    if (index > -1) {
      this.selectedLabelSetOptions2.splice(index, 1); // Deselect, no minimum
    } else {
      this.selectedLabelSetOptions2.push(option); // Select
    }
    this.generalSettings.displayLabels = this.selectedLabelSetOptions2;
    this.emitSettingsChange();
  }

  openColorSchemePickerModal(): void {
    this.verifySurveyModal.show();
  }

  cancelColorSchemePickerModal(): void {
    this.verifySurveyModal.hide();
  }

  filterLabelSetOptions(): string[] {
    return this.labelSetOptions.filter(
      (option) => !this.selectedLabelSetOptions.includes(option)
    );
  }

  get showFilter(): boolean {
    return (
      this.generalSettings?.showFilter || BaseChartSettings.defaults.showFilter
    );
  }

  set showFilter(value: boolean) {
    this.generalSettings.showFilter = value;
    this.emitSettingsChange();
  }

  get paginate(): boolean {
    return (
      this.generalSettings?.paginate || BaseChartSettings.defaults.paginate
    );
  }

  set paginate(value: boolean) {
    this.generalSettings.paginate = value;
    this.emitSettingsChange();
  }

  get pageSize(): number {
    return (
      this.generalSettings?.pageSize || BaseChartSettings.defaults.pageSize
    );
  }

  set pageSize(value: number) {
    this.generalSettings.pageSize = value;
    this.emitSettingsChange();
  }

  get showAllData(): boolean {
    return (
      this.generalSettings?.showAllData ||
      BaseChartSettings.defaults.showAllData
    );
  }

  set showAllData(value: boolean) {
    this.generalSettings.showAllData = value;
    this.emitSettingsChange();
  }

  infoText: Record<string, string> = {
    chartType:
      "Choose how you want your data to look. For example, a bar chart shows columns, a line chart shows trends over time, and a pie chart shows parts of a whole. Pick the style that helps you understand your data best.",
    showFilter:
      "Turn this on to add a search box above your list. You can type in the box to quickly find rows that match what you type.",
    paginate:
      "If you have a lot of data, this splits it into pages so it's easier to look through. You can move between pages to see more results.",
    pageSize:
      "Set how many rows you want to see on each page when paging is turned on. For example, if you set this to 20, you'll see 20 rows per page.",
    showAllData:
      "Show all your data at once, instead of just the top results or a sample. This can be helpful if you want to see everything, but might be slower with lots of data.",
    responsive:
      "Makes the chart automatically resize to fit your screen or window, so it always looks good.",
    colorScheme:
      "Pick a set of colors for your chart. This changes how the bars, lines, or sections look, making it easier to tell things apart.",
    backgroundColor:
      "Change the background color behind your chart. This can help your chart stand out or match your page.",
    showBorder:
      "Add lines between sections in pie or doughnut charts to make them easier to see.",
    padding: "Add extra space around your chart so things don't look crowded.",
    showLegend:
      "Show or hide the legend, which explains what the colors or symbols in your chart mean.",
    legendPosition:
      "Choose where the legend appears: above, below, to the left, or to the right of your chart.",

    legendLabelsChooser:
      "Pick which details or columns you want to see in the legend and when you hover over chart elements. For lists, this controls which columns are shown. This affects what information appears in tooltips when you hover over data points and what shows up in the chart legend.",
    datapointLabelsChooser:
      "Choose which details are displayed directly on the chart as text labels on each data point. These labels appear permanently on the chart, not just when hovering. Select from the columns you've chosen in the Legend/Hover Labels section above.",

    // Data labels (Chart.js Data Labels plugin semantics)
    showChartLabels:
      "Show numbers or values directly on the chart, so you can see exact amounts without hovering.",
    labelFontColor:
      "Pick the color for the numbers or labels shown on your chart.",
    labelFontSize:
      "Set how big the numbers or labels should be on your chart.",
    labelAnchorPosition:
      "Choose where the label attaches to the chart shape: in the center, at the start, or at the end.",
    labelAlignment:
      "Set how the label lines up with its anchor point: centered, to one side, or in a specific direction.",
    labelOffset:
      "Move the label a little away from its anchor point. This can help if things look crowded.",

    // Color scheme modal
    minColors:
      "This is the minimum number of different colors your chosen color set must have. If your chart has more items than colors, some colors will repeat.",
    modalColorScheme:
      "Pick a color set from the list. Click any row to see a preview and select it.",

    // Common chart filter settings
    timePeriod:
      "Pick a time period to see data for that range. For example, you can look at results from the last week, month, or year. This helps you understand when things are happening.",
    expirationTimePeriod:
      "Choose how long a session should last before it is considered expired. For example, if you select '30 minutes', any session that has not been active for 30 minutes will be considered ended. This helps you see which sessions are currently active.",
    minDownloads:
      "Set the minimum number of downloads to filter the results. For example, if you only want to see items that have been downloaded at least 10 times, enter 10 here. This helps you focus on the most popular items.",
    minOpened:
      "Set the minimum number of times a manual must be opened to appear in the list. For example, if you enter 5, you will only see manuals that were opened at least 5 times. This helps you focus on the most used manuals.",
    minViewed:
      "Set the minimum number of times a page must be viewed to appear in the list. For example, if you enter 5, you will only see pages that were viewed at least 5 times. This helps you focus on the most visited pages.",
    minUsed:
      "Set the minimum number of times a search term must be used to appear in the list. For example, if you enter 5, you will only see search terms that were used at least 5 times. This helps you focus on the most popular search terms people are using.",
    numberResults:
      "Set how many items you want to see in the list. For example, if you enter 10, you will see the top 10 items. This helps you focus on the most or least popular items, depending on the chart.",
    userPermission:
      "Select the type of user permission to filter results. For example, you can choose to see data only for administrators, editors, or regular users. This helps you focus on the activity of specific user groups.",
  };

  info(key: string): string {
    return this.infoText[key] ?? "No description available.";
  }

  onInfoClick(evt: MouseEvent, tooltipKey?: string) {
    evt.stopPropagation();

    if (!tooltipKey) {
      const button = evt.target as HTMLButtonElement;
      const titleAttr = button.getAttribute("title");
      tooltipKey = Object.keys(this.infoText).find(
        (key) => this.info(key) === titleAttr
      );
    }

    if (tooltipKey) {
      this.tooltipCoordinator.handleInfoClick(evt, tooltipKey, this.infoText);
    }
  }

  isTooltipVisible(key: string): boolean {
    return this.tooltipCoordinator.isTooltipVisible(key);
  }

  hideTooltip(): void {
    this.tooltipCoordinator.hideAllTooltips();
  }

  ngOnInit(): void {
    // Check if chartDetails is defined and has a valid chartTypeOptions array with values
    if (
      this.chartDetails?.chartTypeOptions &&
      Array.isArray(this.chartDetails.chartTypeOptions) &&
      this.chartDetails.chartTypeOptions.length > 0
    ) {
      this.chartTypeOptions = this.chartDetails.chartTypeOptions; // Use the subset from chartDetails
    } else {
      this.chartTypeOptions = Object.values(DisplayChartType); // Default to all DisplayChartType values
    }

    if (
      this.chartDetails?.labelOptions &&
      Array.isArray(this.chartDetails.labelOptions)
    ) {
      this.labelSetOptions = this.chartDetails.labelOptions;
    }

    if (this.chartDetails?.customLabels) {
      Object.keys(this.chartDetails.customLabels).forEach((key) => {
        if (!this.labelSetOptions.includes(key)) {
          this.labelSetOptions.push(key);
        }
      });
    }
  }

  ngOnDestroy(): void {}
}
